import React, { useState, useEffect, useRef, useId } from 'react';
import { useTranslation } from 'react-i18next';
import clientService from '../services/clientService';

/**
 * ClientSearch Component
 * Provides autocomplete search for clients with debouncing.
 * Displays client name, tax ID, and phone in suggestions.
 */
const ClientSearch = ({
  onSelect,
  selectedClient = null,
  disabled = false
}) => {
  const { t } = useTranslation();
  // Stable id so the combobox input can point aria-controls at its listbox.
  const listboxId = useId();
  const [searchInput, setSearchInput] = useState(selectedClient?.name || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState('');

  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  /**
   * Keep the visible input text in sync when selectedClient changes
   * programmatically (e.g. after creating a client inline via the modal),
   * not just via an interactive suggestion click.
   */
  useEffect(() => {
    setSearchInput(selectedClient?.name || '');
  }, [selectedClient?.name]);

  /**
   * Handle client search with debouncing (300ms).
   * Fetches clients from API based on search query.
   */
  const handleSearch = async (query) => {
    setSearchInput(query);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Clear suggestions if query is empty
    if (!query.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      setError('');
      return;
    }

    // Debounce 300ms
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setIsLoading(true);
        setError('');

        const response = await clientService.listClients(null, null, query);

        const results = (response.data.results || []).slice(0, 10);
        setSuggestions(results);
        setShowSuggestions(true);
      } catch (err) {
        console.error('Error searching clients:', err);
        setSuggestions([]);
        setError(t('common.failedToLoad'));
      } finally {
        setIsLoading(false);
      }
    }, 300);
  };

  /**
   * Handle client selection from suggestions.
   * Closes suggestions dropdown and calls parent onSelect callback.
   */
  const handleSelectClient = (client) => {
    setSearchInput(client.name);
    setSuggestions([]);
    setShowSuggestions(false);
    setError('');
    onSelect(client);
  };

  /**
   * Close suggestions dropdown when clicking outside.
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickOutside =
        inputRef.current && !inputRef.current.contains(event.target) &&
        suggestionsRef.current && !suggestionsRef.current.contains(event.target);

      if (isClickOutside) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Clear timeout on unmount
   */
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="entity-search-container">
      <input
        ref={inputRef}
        type="text"
        value={searchInput}
        onChange={(e) => handleSearch(e.target.value)}
        onFocus={() => searchInput.trim() && suggestions.length > 0 && setShowSuggestions(true)}
        placeholder={t('clientQuotes.searchClientPlaceholder')}
        disabled={disabled}
        className="form-input"
        role="combobox"
        aria-label={t('clientQuotes.searchClientPlaceholder')}
        aria-autocomplete="list"
        aria-expanded={showSuggestions}
        aria-controls={listboxId}
      />

      {isLoading && (
        <div className="entity-search-loading" role="status">
          <span className="loading-spinner"></span>
          {t('common.loading')}...
        </div>
      )}

      {error && (
        <div className="entity-search-error" role="alert">
          {error}
        </div>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          id={listboxId}
          className="entity-search-suggestions"
          role="listbox"
        >
          {suggestions.map((client) => (
            <div
              key={client.id}
              onClick={() => handleSelectClient(client)}
              className="entity-suggestion-item"
              role="option"
              aria-selected={selectedClient?.id === client.id}
            >
              <div className="entity-suggestion-name">{client.name}</div>
              <div className="entity-suggestion-meta">
                <span className="entity-suggestion-detail">{client.tax_id}</span>
                {' • '}
                <span>{client.phone}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showSuggestions && searchInput.trim() && suggestions.length === 0 && !isLoading && (
        <div className="entity-search-empty" role="status">
          {t('clientQuotes.noClientsFound')}
        </div>
      )}
    </div>
  );
};

export default ClientSearch;
